/**
 * @file ResetPasswordView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Backdrop, Box, Button, Grid, Avatar , Paper, Typography, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import "./../Login.css"
import { Navigate } from "react-router-dom";
import { ResetPasswordDescTheme, SignButtonTheme, SignLabelTheme } from "../LoginTheme";
import Snackbar from "@mui/material/Snackbar";
import { AppProgressBar } from "../../utils/AppProgressBar";
import { AppAlert } from "../../utils/AppAlert";
import { ResetPasswordViewModel } from "../viewmodels/ResetPasswordViewModel";
import { LocationComponent } from "../../utils/LocationComponent";
import { TextFieldWrapper } from "../../common/customviews/TextFieldWrapper";
import { AppUtility } from "../../utils/AppUtility";
import { roboto500Text16 } from "../../utils/theme/CommonTheme";
import { BaseComponent } from "../../home/base/BaseComponent";
import { ApiError } from "../../../services/models/ApiError";
import { PasswordSentSuccess } from "../../utils/AppConstant";

/**
 * A type of Component state
 */
type CompState = {
    errorToggle: boolean, backDrop: boolean, submitted: boolean, errorMessage: string, isErrorAlert: boolean, username: string,
    disableBtn: boolean, emailValidationError: string
}

/**
 * An interface definition for Component Props
 */
interface Props {
    viewModel: ResetPasswordViewModel,
    onForgotPasswordFinished: any
}

/**
 * A class definition for ResetPasswordView
 */
export class ResetPasswordView extends BaseComponent<Props, CompState> {

    location?: Location;

    constructor(props: Props) {
        super(props)
        this.state = {
            errorToggle: false, backDrop: false, submitted: false, isErrorAlert: true,
            errorMessage: "", username: "", disableBtn: true, emailValidationError: ""
        };
    }

    /**
     * Hides loading
     */
    private hideLoading = () => {
        this.setState({ backDrop: false })
    }

    /**
     * Shows loading
     */
    private showLoading = () => {
        this.setState({ backDrop: true })
    }

    /**
     * Hides error
     */
    private hideError = () => {
        this.setState({ errorToggle: false })
    }

    /**
     * Toggler Alert Type
     * @param errorAlert boolean
     */
    private toggleAlertType = (errorAlert: boolean) => {
        this.setState({ isErrorAlert: errorAlert })
    }

    /**
     * shows errors
     * @param message string
     */
    private showError = (message: string) => {
        this.setState({ errorToggle: true, errorMessage: message, backDrop: false })
    }

    /**
     * Submits credentials
     * @returns 
     */
    private submitCredentials = async () => {
        if (!this.state.username) {
            const error = "Empty email.";
            this.toggleAlertType(true);
            this.showError(error);
            return;
        }
        this.props.viewModel.onPasswordSent.subscribe((response: any)=>{
            this.logger.verbose(`submitCredentials() - ${response}`, ResetPasswordView.name);
            this.toggleAlertType(false);
            this.showError(PasswordSentSuccess);
            this.hideLoading();
            setTimeout(()=>{ this.dismissResetView();}, 1000);
        });

        this.props.viewModel.onPasswordSentError.subscribe((error: ApiError) => {
            this.logger.verbose(JSON.stringify(error), ResetPasswordView.name);
            this.toggleAlertType(true);
            this.showError(error.message!!); //eslint-disable-line
            this.hideLoading();
        });
        this.showLoading();
        this.props.viewModel.generateResetPasswordLink(this.state.username);
    }

    /**
     * Handles location updates
     * @param location Location
     */
    private handleLocationUpdate = (location: Location) => {
        this.location = location;
    }

    /**
     * Dismisses reset view
     */
    private dismissResetView = () => {
        this.props.onForgotPasswordFinished();
    }

    /**
     * Triggers on change of username
     * @param username string
     */
    private onChangeUsername = (username: string) => {
        const isValidEmail = AppUtility.validateEmail(username);
        isValidEmail ? this.resetEmailError() : this.setEmailError();
        this.setState({ username: username, disableBtn: !isValidEmail })
    }

    /**
     * Sets email"s error
     */
    private setEmailError = () => {
        this.setState({ emailValidationError: "Please enter a valid email address" })
    }

    /**
     * Resets email errors
     */
    private resetEmailError = () => {
        this.setState({ emailValidationError: "" })
    }

    render() {

        return <React.Fragment>
            <div className="topcorner">
                <IconButton className="CloseButton" onClick={this.dismissResetView}>
                    <Avatar src={"/../closeIcon.svg"} />
                </IconButton>
            </div>
            <div className="LoginFormContent">
                {<LocationComponent onChange={this.handleLocationUpdate} />}
                {this.state.submitted && <Navigate to="/home" replace={true}></Navigate>}
                <Box
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 1,
                            border: "0px" //"1px dashed grey"
                        },
                    }}>
                    <Paper variant="outlined" style={{width:"75%"}}>
                        <ThemeProvider theme={SignLabelTheme}>
                            <Typography className="SignInLabel">Reset password</Typography>
                        </ThemeProvider>
                        <ThemeProvider theme={ResetPasswordDescTheme}>
                            <Typography className="ForgotPasswordSubtitle">Enter the email you signed up with, and we’ll send you a link to reset your password.</Typography>
                        </ThemeProvider>
                        <ThemeProvider theme={roboto500Text16}>
                                <TextFieldWrapper id="outlined-basic" label="Enter your email" variant="outlined" onChange={(e) => { this.onChangeUsername(e.target.value) }}
                                    className="ResetTextField" size="medium" fullWidth autoFocus
                                    error={this.state.emailValidationError.length === 0 ? false : true}
                                    helperText={this.state.emailValidationError}/>
                        </ThemeProvider>
                        <Grid container justifyContent="center" alignItems="center" >
                            <ThemeProvider theme={SignButtonTheme}>
                                <Button variant="contained" onClick={this.submitCredentials}
                                    className="ResetButton" disabled={this.state.disableBtn} disableElevation>
                                    Reset
                                </Button>
                            </ThemeProvider>
                            <Grid item xs={12} justifyContent="center" alignItems="center" container>
                                <ThemeProvider theme={SignButtonTheme}>
                                    <Button onClick={this.dismissResetView} className="NeverMindButton" size="small">Never mind, I remembered it!</Button>
                                </ThemeProvider>
                            </Grid>
                        </Grid>
                    </Paper>

                </Box>
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={this.state.backDrop}
                    onClick={this.hideLoading}>
                    <AppProgressBar color="primary" />
                </Backdrop>
            </div>

            <Snackbar open={this.state.errorToggle} autoHideDuration={6000} onClose={this.hideError}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                <AppAlert onClose={this.hideError} severity={this.state.isErrorAlert ? "error" : "success"} sx={{ width: "150%" }}>
                    {`${this.state.errorMessage}`}
                </AppAlert>
            </Snackbar>
        </React.Fragment>
    }
}